/* @refresh reload */
import { render } from "solid-js/web";

import { setServiceWorkerUpdate } from "@tendly/shared";

import { App } from "@/app";
import { isTauriRuntime } from "@/lib/platform";
import { hydrateSecureTokens, primeActiveNativeToken } from "@/lib/secure-token";
import "@/styles/global.css";

// PWA: auto-update with a SINGLE reload. `sw.js` (Workbox) already calls `skipWaiting()`
// upon receiving `{type:"SKIP_WAITING"}`; here, when a new SW is INSTALLED and a controller exists
// (an update, not the first install), we request it → it takes control → `controllerchange`
// → we reload ONCE. One user refresh is enough (previously the new SW stayed `waiting`).
// Intentionally dependency-free (avoids workbox-window). To make it FULLY automatic, see
// infra/PWA_UPDATES.md (reload inactive tabs without user action). Tauri has no SW → no-op.
/**
 * Telemetry beacon for PWA updates (docs/CLIENT_TELEMETRY.md).
 *
 * The outcome has to survive a page reload, because the successful outcome IS a
 * reload: recording "applied" in module state and then calling `location.reload()`
 * would throw the value away a millisecond later, and the beacon would report
 * "applied" exactly never. So the hand-off goes through `sessionStorage` (per
 * tab, cleared on read). Nothing personal goes in it — one of four literal words.
 *
 * Wrapped because `sessionStorage` throws outright in a partitioned/blocked
 * third-party context, and a telemetry beacon may not take the app down.
 */
const SW_UPDATE_HANDOFF_KEY = "sw-update";

function rememberServiceWorkerUpdate(state: string): void {
  try {
    globalThis.sessionStorage?.setItem(SW_UPDATE_HANDOFF_KEY, state);
  } catch {
    // Storage denied — the beacon degrades to "none", the app is unaffected.
  }
}

if ("serviceWorker" in navigator) {
  // Read the hand-off left by the pre-reload page, if any.
  try {
    const carried = globalThis.sessionStorage?.getItem(SW_UPDATE_HANDOFF_KEY);
    if (carried === "applied") {
      setServiceWorkerUpdate("applied");
      globalThis.sessionStorage?.removeItem(SW_UPDATE_HANDOFF_KEY);
    }
  } catch {
    // Same rule: unreadable storage is not an error worth surfacing.
  }

  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) {
      return;
    }
    reloading = true;
    rememberServiceWorkerUpdate("applied");
    window.location.reload();
  });
  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // In environments where the SW is blocked (e.g. Playwright with
        // `serviceWorkers: "block"`) or denied, `register()` may resolve without
        // a usable registration. Without this guard, `reg.addEventListener`
        // would throw "Cannot read properties of undefined" as a pageerror.
        if (!reg) {
          return undefined;
        }
        reg.addEventListener("updatefound", () => {
          const next = reg.installing;
          if (!next) {
            return;
          }
          next.addEventListener("statechange", () => {
            // Only on UPDATE (a controller already exists); do not reload on the first install.
            if (next.state === "installed" && navigator.serviceWorker.controller) {
              // A new build is installed but not yet in control. If the device
              // keeps reporting "pending" it means the swap is STUCK — the exact
              // "we shipped a fix and nobody got it" case the census exists for.
              setServiceWorkerUpdate("pending");
              // `next` is a ServiceWorker, not a Window: its `postMessage(message, transfer?)`
              // does NOT accept targetOrigin (the 2nd argument is the transfer list). The rule,
              // designed for Window.postMessage, is a false positive here.
              // oxlint-disable-next-line unicorn/require-post-message-target-origin
              next.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
        return undefined;
      })
      // A blocked/denied registration rejects the promise; swallowing it avoids
      // an unhandledrejection that pollutes the console (and breaks "no severe
      // errors" assertions). PWA is progressive enhancement: if it does not
      // register, the app works the same.
      .catch(() => {
        // Beacon only: still swallowed, still no user-visible effect. A fleet
        // where this climbs means updates cannot reach anyone, which is
        // invisible from the server without this signal.
        setServiceWorkerUpdate("failed");
        return undefined;
      });
  });
}

const root = document.querySelector("#root");

if (!root) {
  throw new Error("Root element #root not found in index.html");
}
const rootEl: Element = root;

function mountApp(): void {
  render(() => <App />, rootEl);

  // Tell the index.html boot watchdog the app mounted: cancels the self-heal
  // reload and clears its one-shot guard (so a future stale-deploy can recover).
  (window as typeof window & { __appBootOk?: () => void }).__appBootOk?.();
}

if (isTauriRuntime()) {
  // Tauri DESKTOP: load the Bearer token(s) from the OS keychain into the
  // synchronous in-memory cache BEFORE the app mounts, so the very first
  // authenticated request resolves its token (keychain reads are async; the
  // hot read-path is sync). Best-effort — never block boot on a keychain error.
  try {
    await hydrateSecureTokens();
    // Phase 2: prime the RUST-held active token from the store so the
    // `api_request` proxy can inject the Bearer on the first authenticated
    // request. The token stays in Rust — JS never receives it.
    await primeActiveNativeToken();
  } catch (err) {
    console.warn("[secure-token] hydration failed, continuing:", err);
  }
}
// Web (cookie-BFF) renders immediately; Tauri renders once the cache is warm.
mountApp();

// Dismiss the first-paint loader baked into index.html once the app shell has
// mounted. We wait two animation frames so the freshly-rendered shell has
// actually painted before we fade the loader, avoiding a flash-to-blank.
function dismissAppLoader() {
  const loader = document.querySelector("#app-loader");
  if (!loader) {
    return;
  }
  loader.classList.add("app-loader--hidden");
  const LOADER_FADE_MS = 300;
  setTimeout(() => {
    loader.remove();
  }, LOADER_FADE_MS);
}
requestAnimationFrame(() => {
  requestAnimationFrame(dismissAppLoader);
});
